import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Clock, ShieldCheck, Users, Sparkles, Calendar, BarChart3,
  LogOut, Mail, Home, ArrowRight, CheckCircle2, Building2, XCircle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientPendingApproval = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showIntake, setShowIntake] = useState(true);
  const [submittingIntake, setSubmittingIntake] = useState(false);
  const [intakeSent, setIntakeSent] = useState(false);
  const [intake, setIntake] = useState({
    company_size: '', industry: '', event_types: '',
    budget_range: '', timeline: '', notes: '',
  });

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    axios
      .get(`${API}/api/client/approval-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => setStatus({ status: 'pending_approval', is_pending: true }))
      .finally(() => setLoading(false));
  }, [navigate]);

  // If actually approved (e.g. they hit this page after approval),
  // bounce them to their dashboard.
  useEffect(() => {
    if (status?.is_approved) navigate('/client/home', { replace: true });
  }, [status, navigate]);

  const submitIntake = async () => {
    setSubmittingIntake(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/client/intake`, intake, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Thanks — we'll include this in your review.");
      setIntakeSent(true);
      setShowIntake(false);
    } catch (e) {
      toast.error('Could not save right now. Please try again.');
    } finally {
      setSubmittingIntake(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const isRejected = status?.is_rejected;
  const isAgency = user?.role === 'agency';
  const accountTypeLabel = isAgency ? 'agency workspace' : 'full-service client';

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-slate-950 to-zinc-900 text-white" data-testid="client-pending-page">
      {/* Top bar */}
      <header className="border-b border-zinc-800/60 backdrop-blur-md bg-zinc-950/40 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img
              src="https://res.cloudinary.com/dzlc6qta0/image/upload/v1771451035/d2e1bd_3ffc4a4bc88e4587b369a584fd8a636b_mv2_pvlple.png"
              alt="iEvntür"
              className="h-8"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (window.location.href = 'https://app.ievntur.com')}
              data-testid="back-home-btn"
            >
              <Home className="h-4 w-4 mr-1" />
              Home
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { logout(); navigate('/login'); }}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 mr-1" />
              Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        {/* Hero */}
        <div className="text-center mb-12">
          {isRejected ? (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-500/10 border border-rose-500/30 mb-6">
              <XCircle className="h-8 w-8 text-rose-400" />
            </div>
          ) : (
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-cyan-500/10 border border-cyan-500/30 mb-6">
              <Clock className="h-8 w-8 text-cyan-400" />
            </div>
          )}
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4"
            data-testid="pending-title"
          >
            {isRejected ? 'Account application update' : (
              <>Your account request has been received</>
            )}
          </h1>
          <p className="text-lg text-zinc-400 max-w-2xl mx-auto leading-relaxed">
            {isRejected ? (
              <>We were unable to approve your {accountTypeLabel} application at this time.</>
            ) : (
              <>Thanks for signing up with <strong className="text-cyan-400">iEvntür</strong>. Your <strong className="text-white">{accountTypeLabel}</strong> account is currently <strong className="text-white">pending approval</strong>. Our team typically reviews within 24 hours.</>
            )}
          </p>
        </div>

        {/* Status card */}
        <Card className="bg-zinc-900/60 border-zinc-800 backdrop-blur-md mb-8">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Building2 className="h-5 w-5 text-zinc-400" />
                  <span className="text-sm text-zinc-400">Account email</span>
                </div>
                <p className="font-mono text-base text-white" data-testid="pending-email">
                  {user?.email}
                </p>
              </div>
              <div className="sm:text-right">
                <div className="flex items-center gap-2 mb-2 sm:justify-end">
                  <Clock className="h-5 w-5 text-zinc-400" />
                  <span className="text-sm text-zinc-400">Estimated review time</span>
                </div>
                <p className="text-base text-white">
                  {isRejected ? 'Review complete' : 'Within 24 hours'}
                </p>
              </div>
            </div>
            {isRejected && status?.reason && (
              <div className="mt-6 p-4 rounded-lg bg-rose-500/10 border border-rose-500/30">
                <p className="text-sm text-rose-300 font-medium mb-1">Reason</p>
                <p className="text-white">{status.reason}</p>
                {status.notes && (
                  <>
                    <p className="text-sm text-rose-300 font-medium mt-3 mb-1">Note from our team</p>
                    <p className="text-zinc-300">{status.notes}</p>
                  </>
                )}
                <p className="text-sm text-zinc-400 mt-4">
                  If you think this was a mistake, reach out to{' '}
                  <a href="mailto:support@ievntur.com" className="text-cyan-400 underline">
                    support@ievntur.com
                  </a>{' '}
                  and we'll take another look.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Iter-313k — Business Verification requirements panel. Surfaces
            exactly which business-verification fields are still missing
            so users don't play "guess what to fix" during the review
            window. Rendered only for non-rejected states with unmet
            requirements. */}
        {!isRejected && user?.business_verification && !user.business_verification.meets_requirements && (
          <Card className="bg-gradient-to-br from-amber-500/10 to-zinc-900/40 border-amber-500/30 mb-8" data-testid="business-verification-panel">
            <CardContent className="p-6 sm:p-8">
              <div className="flex items-start gap-3 mb-4">
                <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 p-2 shrink-0">
                  <ShieldCheck className="h-5 w-5 text-amber-400" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-1">
                    Business verification requirements
                  </h3>
                  <p className="text-sm text-zinc-400">
                    We need the following to complete your business verification. Providing these speeds up your approval.
                  </p>
                </div>
              </div>
              <ul className="space-y-2 mb-4" data-testid="business-verification-missing">
                {user.business_verification.missing_fields.includes('business_email') && (
                  <li className="flex items-start gap-2 text-sm text-amber-100" data-testid="bv-missing-business_email">
                    <XCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-white">Business email address</strong> — free-webmail domains (Gmail, Yahoo, Outlook, iCloud) can&apos;t be verified. Please use an email at your company&apos;s domain.
                    </span>
                  </li>
                )}
                {user.business_verification.missing_fields.includes('company_website') && (
                  <li className="flex items-start gap-2 text-sm text-amber-100" data-testid="bv-missing-company_website">
                    <XCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <span>
                      <strong className="text-white">Company website</strong> — a public site helps us confirm your business is real. You can add this any time in Profile → About.
                    </span>
                  </li>
                )}
                {user.business_verification.missing_fields.includes('company_name') && (
                  <li className="flex items-start gap-2 text-sm text-amber-100" data-testid="bv-missing-company_name">
                    <XCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Company name</strong></span>
                  </li>
                )}
                {user.business_verification.missing_fields.includes('contact_name') && (
                  <li className="flex items-start gap-2 text-sm text-amber-100" data-testid="bv-missing-contact_name">
                    <XCircle className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
                    <span><strong className="text-white">Primary contact name</strong></span>
                  </li>
                )}
              </ul>
              <p className="text-xs text-zinc-500">
                Once submitted, our team will re-review within 24 hours. Reach out to{' '}
                <a href="mailto:support@ievntur.com" className="text-cyan-400 underline">support@ievntur.com</a>{' '}
                if you have questions about any requirement.
              </p>
            </CardContent>
          </Card>
        )}

        {/* What you'll be able to do */}
        {!isRejected && (
          <>
            <h2 className="text-2xl font-semibold mb-6 text-white">
              Once approved, you'll be able to:
            </h2>
            <div className="grid md:grid-cols-2 gap-4 mb-12">
              {[
                { icon: Users, title: 'Browse and book event talent nationwide', desc: 'Search verified bartenders, servers, brand ambassadors, promo models, production staff and experiential talent.' },
                { icon: Calendar, title: 'Post gigs and manage staffing in real time', desc: 'Create shifts, invite talent, lock confirmations, and track attendance from one dashboard.' },
                { icon: Sparkles, title: 'AI-powered matching & event tools', desc: 'Let our matching engine surface the right talent for each role automatically.' },
                { icon: BarChart3, title: 'Manage bookings, approvals & comms', desc: 'Approve hours, run invoicing, see ratings, and message your team — all in one place.' },
              ].map((item, i) => (
                <div
                  key={i}
                  className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5 hover:border-cyan-500/40 transition-colors"
                  data-testid={`benefit-${i}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="rounded-lg bg-cyan-500/10 border border-cyan-500/30 p-2 shrink-0">
                      <item.icon className="h-5 w-5 text-cyan-400" />
                    </div>
                    <div>
                      <h3 className="font-medium text-white mb-1">{item.title}</h3>
                      <p className="text-sm text-zinc-400 leading-relaxed">{item.desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Why we review */}
            <Card className="bg-zinc-900/40 border-zinc-800 mb-8">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-start gap-4">
                  <ShieldCheck className="h-6 w-6 text-cyan-400 mt-1 shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">
                      Why is my account under review?
                    </h3>
                    <p className="text-zinc-400 leading-relaxed">
                      iEvntür is a quality-first marketplace. We verify every business account to
                      protect both clients and talent — so that when you book talent, you can
                      trust they're who they say they are, and when talent works your event, they
                      know it's a real, paid gig. Our team typically reviews new accounts within
                      24 hours.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Intake form */}
            <Card className="bg-gradient-to-br from-cyan-500/5 to-zinc-900/40 border-cyan-500/30 mb-8">
              <CardContent className="p-6 sm:p-8">
                {!showIntake && !intakeSent && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                      <h3 className="text-lg font-semibold text-white mb-1">
                        Want to speed up your review?
                      </h3>
                      <p className="text-zinc-400 text-sm">
                        Tell us a bit more about your company and event needs — it helps our team
                        get you the right access faster.
                      </p>
                    </div>
                    <Button
                      onClick={() => setShowIntake(true)}
                      className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950 shrink-0"
                      data-testid="open-intake-btn"
                    >
                      Tell us more <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </div>
                )}
                {intakeSent && (
                  <div className="flex items-center gap-3 text-emerald-400">
                    <CheckCircle2 className="h-6 w-6" />
                    <p className="font-medium">
                      Thanks — your team will see this during review.
                    </p>
                  </div>
                )}
                {showIntake && (
                  <div className="space-y-4" data-testid="intake-form">
                    <h3 className="text-lg font-semibold text-white">
                      {isAgency
                        ? 'Tell us about your agency & talent roster'
                        : 'Tell us about your company & event needs'}
                    </h3>
                    <p className="text-sm text-zinc-400 -mt-1">
                      These details help our team approve your account faster. Required so we can match you with the right resources.
                    </p>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="company_size" className="text-zinc-300">Company size</Label>
                        <Select
                          value={intake.company_size}
                          onValueChange={(v) => setIntake({ ...intake, company_size: v })}
                        >
                          <SelectTrigger id="company_size" className="bg-zinc-950 border-zinc-700 text-white" data-testid="intake-company-size">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1-10">1-10 employees</SelectItem>
                            <SelectItem value="11-50">11-50 employees</SelectItem>
                            <SelectItem value="51-200">51-200 employees</SelectItem>
                            <SelectItem value="201-1000">201-1,000 employees</SelectItem>
                            <SelectItem value="1000+">1,000+ employees</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="industry" className="text-zinc-300">Industry</Label>
                        <Input
                          id="industry"
                          className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500"
                          placeholder="e.g. Beverage, Hospitality, Events"
                          value={intake.industry}
                          onChange={(e) => setIntake({ ...intake, industry: e.target.value })}
                          data-testid="intake-industry"
                        />
                      </div>
                      <div>
                        <Label htmlFor="event_types" className="text-zinc-300">Event types</Label>
                        <Input
                          id="event_types"
                          className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500"
                          placeholder="e.g. trade shows, brand activations"
                          value={intake.event_types}
                          onChange={(e) => setIntake({ ...intake, event_types: e.target.value })}
                          data-testid="intake-event-types"
                        />
                      </div>
                      <div>
                        <Label htmlFor="budget_range" className="text-zinc-300">Typical event budget</Label>
                        <Select
                          value={intake.budget_range}
                          onValueChange={(v) => setIntake({ ...intake, budget_range: v })}
                        >
                          <SelectTrigger id="budget_range" className="bg-zinc-950 border-zinc-700 text-white" data-testid="intake-budget">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="<5k">Under $5,000</SelectItem>
                            <SelectItem value="5-25k">$5,000 - $25,000</SelectItem>
                            <SelectItem value="25-100k">$25,000 - $100,000</SelectItem>
                            <SelectItem value="100k+">$100,000+</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="timeline" className="text-zinc-300">When is your next event?</Label>
                        <Input
                          id="timeline"
                          className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500"
                          placeholder="e.g. Next month, Q3 2026, ongoing"
                          value={intake.timeline}
                          onChange={(e) => setIntake({ ...intake, timeline: e.target.value })}
                          data-testid="intake-timeline"
                        />
                      </div>
                      <div className="sm:col-span-2">
                        <Label htmlFor="notes" className="text-zinc-300">Anything else?</Label>
                        <Textarea
                          id="notes"
                          rows={3}
                          className="bg-zinc-950 border-zinc-700 text-white placeholder:text-zinc-500"
                          placeholder="Tell us about specific talent types, locations, or unique requirements."
                          value={intake.notes}
                          onChange={(e) => setIntake({ ...intake, notes: e.target.value })}
                          data-testid="intake-notes"
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="ghost"
                        onClick={() => setShowIntake(false)}
                        data-testid="intake-cancel-btn"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={submitIntake}
                        disabled={submittingIntake}
                        className="bg-cyan-500 hover:bg-cyan-400 text-zinc-950"
                        data-testid="intake-submit-btn"
                      >
                        {submittingIntake ? 'Sending...' : 'Send to review team'}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {/* Contact / Support */}
        <div className="text-center text-zinc-400 text-sm">
          <p>
            Questions? Reach our team at{' '}
            <a
              href="mailto:support@ievntur.com"
              className="text-cyan-400 hover:underline inline-flex items-center gap-1"
              data-testid="support-email"
            >
              <Mail className="h-3.5 w-3.5" />
              support@ievntur.com
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default ClientPendingApproval;
